import {
  isAnyRole,
  isAuditor,
  isEmployeeOnly,
  isLead,
  isManager,
  isSchedulingAdminOrHigher,
  isSuperAdmin,
  isSupport,
} from "@/auth/auth.roles";
import { auth } from "@/stores/auth.store";
import { createRouter, createWebHistory } from "vue-router";
import { hook } from "./helpers";
import useEnvCheck from "@/composables/useEnvCheck";

const IS_NODE_ENV_DEV = import.meta.env.DEV;
const { envCheck } = useEnvCheck();
export const IS_STAGING = envCheck() === "STAGE";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  linkActiveClass: "vue-w-active-link",
  routes: [
    {
      path: "/:pathMatch(.*)*",
      name: "NotFound",
      component: () => import("@/router/views/home/HomeView.vue"),
      meta: {
        requiresAuth: false,
        checkRoles: isAnyRole,
        title: "Home",
      },
    },
    {
      path: "/",
      name: "home",
      component: () => import("@/router/views/home/HomeView.vue"),
      meta: {
        requiresAuth: false,
        checkRoles: isAnyRole,
        title: "Home",
      },
    },
    {
      ...hook("/login", auth.login),
      meta: {
        requiresAuth: false,
        checkRoles: isAnyRole,
        title: "Home",
      },
    },
    {
      ...hook("/logout", auth.logout),
      meta: {
        requiresAuth: false,
        checkRoles: isAnyRole,
        title: "Home",
      },
    },
    {
      path: "/admin",
      name: "admin",
      meta: {
        requiresAuth: true,
        checkRoles: isSchedulingAdminOrHigher,
        title: "Admin",
      },
      children: [
        {
          path: "schedule",
          name: "build-schedule",
          component: () =>
            import(
              "@views/admin/build-schedule/BuildSchedule.vue"
            ),
          meta: {
            requiresAuth: true,
            checkRoles: isSchedulingAdminOrHigher,
            title: "Schedule",
          },
        },
        {
          path: "backups",
          name: "backups",
          component: () =>
            import(
              "@views/admin/backups/BackupSchedules.vue"
            ),
          meta: {
            requiresAuth: true,
            checkRoles: isSchedulingAdminOrHigher,
            title: "Backups",
          },
        },
        {
          path: "schedule-config",
          name: "schedule-config",
          component: () =>
            import(
              "@views/admin/sched-config/SchedConfigList.vue"
            ),
          meta: {
            requiresAuth: true,
            checkRoles: isSchedulingAdminOrHigher,
            title: "Schedule config",
          },
        },
        {
          path: "shift-codes",
          name: "shift-codes",
          component: () =>
            import("@views/admin/shift-code/ShiftCodeList.vue"),
          meta: {
            requiresAuth: true,
            checkRoles: isSchedulingAdminOrHigher,
            title: "Shift codes",
          },
        },
        {
          path: "user-list",
          name: "user-list",
          component: () =>
            import("@views/admin/user-admin/UserList.vue"),
          meta: {
            requiresAuth: true,
            checkRoles: isSchedulingAdminOrHigher,
            title: "User list",
          },
        },
        {
          path: "team-user-defaults",
          name: "team-user-defaults",
          component: () =>
            import(
              "@views/admin/user-admin/TeamUserDefaultsManage.vue"
            ),
          meta: {
            requiresAuth: true,
            checkRoles: isSchedulingAdminOrHigher,
            title: "Team/User defaults",
          },
        },
        {
          path: "team-admin",
          name: "team-admin",
          component: () =>
            import("@views/admin/team-admin/TeamList.vue"),
          meta: {
            requiresAuth: true,
            checkRoles: isSchedulingAdminOrHigher,
            title: "Team management",
          },
        },
        {
          path: "banked-hours",
          name: "banked-hours",
          component: () =>
            import(
              "@views/admin/banked-hours/BankedHours.vue"
            ),
          meta: {
            requiresAuth: true,
            checkRoles: isSchedulingAdminOrHigher,
            title: "Banked hours",
          },
        },
        {
          path: "vacations",
          name: "vacations",
          component: () =>
            import("@views/admin/vacations/VacationsPage.vue"),
          meta: {
            requiresAuth: true,
            checkRoles: isSchedulingAdminOrHigher,
            title: "Vacations",
          },
        },
      ],
    },
    {
      path: "/reviewer",
      name: "reviewer",
      meta: {
        requiresAuth: true,
        checkRoles: () => !isEmployeeOnly(),
        title: "Reviewer",
      },
      children: [
        {
          path: "schedule",
          name: "reviewer-schedule",
          component: () =>
            isAuditor()
              ? import("@views/reviewer-schedule/AuditorSchedule.vue")
              : import("@views/reviewer-schedule/ReviewerSchedule.vue"),
          meta: {
            requiresAuth: true,
            checkRoles: () => !isEmployeeOnly(),
            title: "Schedule",
          },
        },
      ],
    },
    {
      path: "/user",
      name: "/user",
      meta: {
        requiresAuth: true,
        checkRoles: () => !isAuditor(),
        title: "User",
      },
      children: [
        {
          path: "bid-entry",
          name: "bid-entry",
          component: () =>
            import("@views/user/bid-entry/BidEntry.vue"),
          meta: {
            requiresAuth: true,
            checkRoles: () => !isAuditor(),
            title: "Bid entry",
            confirm: true,
          },
        },
        {
          path: "work-schedule",
          name: "work-schedule",
          component: () =>
            import("@views/user/work-schedule/WorkSchedule.vue"),
          meta: {
            requiresAuth: true,
            checkRoles: () => !isAuditor(),
            title: "Work schedule",
          },
        },
      ],
    },
    {
      path: "/live-schedule",
      name: "live-schedule",
      meta: {
        requiresAuth: true,
        checkRoles: () => isLead() || isSupport() || isManager() || isSchedulingAdminOrHigher(),
        title: "Live schedule",
      },
      children: [
        {
          path: "schedule",
          name: "live-schedule-view",
          component: () =>
            import("@/router/views/live-schedule/LiveSchedule.vue"),
          meta: {
            requiresAuth: true,
            checkRoles: () => isLead() || isSupport() || isManager() || isSchedulingAdminOrHigher(),
            title: "Schedule",
          },
        },
        {
          path: "schedmods",
          name: "sched-mods",
          component: () =>
            import("@/router/views/live-schedule/SchedMods.vue"),
          meta: {
            requiresAuth: true,
            checkRoles: () => isLead() || isSupport() || isManager() || isSchedulingAdminOrHigher(),
            title: "Live Schedule Reports",
          },
        },
      ],
    },
    {
      path: "/testing",
      name: "/testing",
      meta: {
        requiresAuth: true,
        checkRoles: () => isSuperAdmin() || IS_NODE_ENV_DEV || IS_STAGING,
        title: "Testing",
      },
      children: [
        {
          path: "styleguide",
          name: "styleguide",
          component: () => import("@/router/views/testing/StyleGuide.vue"),
          meta: {
            requiresAuth: true,
            checkRoles: () => isSuperAdmin() || IS_NODE_ENV_DEV,
            title: "Style Guide",
          },
        },
        {
          path: "datetime",
          name: "datetime",
          component: () => import("@/router/views/testing/DateAndTime.vue"),
          meta: {
            requiresAuth: true,
            checkRoles: () => isSuperAdmin() || IS_NODE_ENV_DEV || IS_STAGING,
            title: "Date & Time",
          },
        },
      ],
    },
  ],
});

router.beforeEach((to) => {
  document.title = `${to.meta.title} - Scheduling app `;
  return true;
});

router.onError((error) => {
  // some deploys do not get caught by AppUpdate
  if (error.message.includes("Failed to fetch dynamically imported module")) {
    location.reload();
  }
});

export default router;
